/** Rate Comparison page with side-by-side carrier cost analysis. */
import React, { useState, useCallback } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Grid,
} from "@mui/material";
import {
  Home as HomeIcon,
  CompareArrows as CompareIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import RateComparisonTable, { RateComparisonResponse } from "../../components/rate-plans/RateComparisonTable";

/** Rate Comparison page component. */
export default function RateComparison() {
  const navigate = useNavigate();
  const [destination, setDestination] = useState("");
  const [resellerSubject, setResellerSubject] = useState<string>("");
  const [resellers, setResellers] = useState<Array<{ id: string; name: string }>>([]);
  const [showComparison, setShowComparison] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Fetch resellers for dropdown. */
  const fetchResellers = useCallback(async () => {
    try {
      // Use relative URL to go through proxy (cookies sent automatically)
      const response = await fetch(`/api/v1/resellers/`, {
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch resellers: ${response.statusText}`);
      }

      const data = await response.json();
      // API shape: { resellers: [...], total, ... } (Wave 5.9 route-order fix)
      setResellers(Array.isArray(data.resellers) ? data.resellers : Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching resellers:", err);
      // Non-critical error, don't show alert
    }
  }, []);

  /** Load resellers on mount. */
  React.useEffect(() => {
    fetchResellers();
  }, [fetchResellers]);

  /** Handle compare button click. */
  const handleCompare = useCallback(() => {
    setError(null);

    // Validate destination
    if (!destination || destination.length < 3) {
      setError("Please enter a valid destination number (at least 3 digits).");
      return;
    }

    if (!/^\d+$/.test(destination)) {
      setError("Destination must contain only numbers.");
      return;
    }

    setShowComparison(true);
  }, [destination]);

  /** Handle clear button click. */
  const handleClear = useCallback(() => {
    setDestination("");
    setResellerSubject("");
    setShowComparison(false);
    setError(null);
  }, []);

  /** Handle comparison clear. */
  const handleComparisonClear = useCallback(() => {
    setShowComparison(false);
  }, []);

  /** Example destinations for help section. */
  const exampleDestinations = [
    { number: "12125551234", description: "New York, USA" },
    { number: "442071234567", description: "London, UK" },
    { number: "61290001234", description: "Sydney, Australia" },
  ];

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumb Navigation */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate("/")}
          sx={{ display: 'flex', alignItems: 'center' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Dashboard
        </Link>
        <Link
          component="button"
          underline="hover"
          color="inherit"
          onClick={() => navigate("/rate-plans")}
        >
          Routing
        </Link>
        <Typography color="text.primary">Rate Comparison</Typography>
      </Breadcrumbs>

      {/* Page Header */}
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Rate Comparison Tool
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Compare carrier costs side-by-side for any destination. Make informed routing decisions
          based on real-time cost analysis.
        </Typography>
      </Box>

      {/* Input Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Destination Number"
              placeholder="e.g., 12125551234"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleCompare();
                }
              }}
              disabled={showComparison}
              helperText="Enter destination number to compare rates"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Reseller (Optional)</InputLabel>
              <Select
                value={resellerSubject}
                label="Reseller (Optional)"
                onChange={(e) => setResellerSubject(e.target.value)}
                disabled={showComparison}
              >
                <MenuItem value="">None (Admin View)</MenuItem>
                {resellers.map((reseller) => (
                  <MenuItem key={reseller.id} value={reseller.id}>
                    {reseller.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={5}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<CompareIcon />}
                onClick={handleCompare}
                disabled={showComparison || !destination}
                size="large"
              >
                Compare
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClear}
                disabled={!showComparison && !destination}
                size="large"
              >
                Clear
              </Button>
            </Box>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Paper>

      {/* Results Section */}
      {showComparison && destination && (
        <Box>
          <RateComparisonTable
            destination={destination}
            resellerSubject={resellerSubject || undefined}
            onClear={handleComparisonClear}
          />
        </Box>
      )}

      {/* Help Section */}
      {!showComparison && (
        <Grid container spacing={3}>
          {/* Example Destinations */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Example Destinations
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Click any example to compare rates:
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  {exampleDestinations.map((example) => (
                    <Button
                      key={example.number}
                      variant="outlined"
                      onClick={() => setDestination(example.number)}
                      sx={{ textTransform: "none", justifyContent: "flex-start" }}
                    >
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {example.number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {example.description}
                        </Typography>
                      </Box>
                    </Button>
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Margin Calculation Explanation */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Understanding Margin Calculation
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  The effective cost is calculated using a hybrid margin formula:
                </Typography>
                <Box
                  sx={{
                    bgcolor: "background.paper",
                    p: 2,
                    borderRadius: 1,
                    fontFamily: "monospace",
                  }}
                >
                  <Typography variant="body2">
                    Margin = max(carrier_cost × margin%, margin_floor)
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Effective Cost = carrier_cost + Margin
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  <strong>Example:</strong>
                  <br />
                  • Carrier Cost: $0.0100
                  <br />
                  • Margin: 20% ($0.0020)
                  <br />• Margin Floor: $0.005
                  <br />
                  • Effective Cost: $0.0120
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Container>
  );
}
